import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
} from '@mui/material'
import GeoPattern from 'geopattern'
import { CSSProperties, FC, PropsWithChildren, useState } from 'react'
import { Icons, InformationModal } from 'utoniq-ui'

import { ThreadBoxFragment } from '@/clients/utoniq-core/schema'
import { ThreadCategory } from '@/components/organisms/community/ThreadCategory'
import { styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell } from '@/core/architecture'
import { useSettingUserInfoModal } from '@/core/membership/useSettingUserInfoModal'

export type ThreadBoxProps = {
  thread: ThreadBoxFragment
  hasPatron: boolean
  isShowModalPersonalInfoRequire: boolean
  fixedHeight?: boolean
  width?: CSSProperties['width']
  onClick?: () => void
}

type ThreadBoxCellProps = PropsWithChildren<
  Pick<ThreadBoxProps['thread'], 'isAccessible' | 'id'> & {
    displayName: string
    isShowModalPersonalInfoRequire: boolean
    artistId: string
    onClick?: () => void
  }
>

export const ThreadBox: FC<ThreadBoxProps> = ({
  thread,
  hasPatron,
  isShowModalPersonalInfoRequire,
  width = '100%',
  onClick,
}) => {
  const isAccessible = thread.isAccessible || hasPatron
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 0,
        width,
        bgcolor: 'transparent',
      }}
    >
      <ThreadBoxCell
        isAccessible={isAccessible}
        isShowModalPersonalInfoRequire={
          isShowModalPersonalInfoRequire && thread.isPremium
        }
        id={thread.id}
        artistId={thread.artist?.id ?? ''}
        displayName={thread.artist?.displayName ?? ''}
        onClick={onClick}
      >
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Stack
            direction="row"
            spacing={3}
            justifyContent="center"
            alignItems="center"
          >
            <Stack sx={{ position: 'relative' }}>
              {thread.thumbnail?.mediumUri ? (
                <>
                  <Avatar
                    variant="square"
                    src={thread.thumbnail?.mediumUri ?? ''}
                    sx={{ width: 85, height: 64, borderRadius: '8px' }}
                  />
                  {thread.isPremium && (
                    <Stack
                      flex={1}
                      sx={{
                        inset: 0,
                        position: 'absolute',
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        borderRadius: '8px',
                      }}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Icons.MembersOnly sx={{ color: 'white' }} />
                    </Stack>
                  )}
                </>
              ) : (
                <>
                  <Box
                    sx={{
                      width: 85,
                      height: 64,
                      borderRadius: '8px',
                      backgroundImage: GeoPattern.generate(
                        thread.id
                      ).toDataUrl(),
                      backgroundSize: '150px',
                      filter: 'contrast(1.3) brightness(1.3)',
                    }}
                  />
                  {thread.isPremium && (
                    <Stack
                      flex={1}
                      sx={{
                        inset: 0,
                        position: 'absolute',
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        borderRadius: '8px',
                      }}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Icons.MembersOnly sx={{ color: 'white' }} />
                    </Stack>
                  )}
                </>
              )}
            </Stack>
            <Stack spacing={2} flex={1} sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={2}>
                <Stack flex={1} spacing={2} sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={styleSets.lineCamp(1)}>
                    {thread.name}
                  </Typography>
                  <Typography variant="caption" sx={styleSets.lineCamp(1)}>
                    {thread.latestComment?.content}
                  </Typography>
                </Stack>
                {!!thread.userUnread?.unreadMentionCount && (
                  <Badge
                    badgeContent={thread.userUnread.unreadMentionCount}
                    color={'like' as any}
                    sx={{
                      position: 'relative',
                      color: 'white',
                      top: 36,
                      right: 15,
                      width: 20,
                      height: 20,
                      '& > span': {
                        fontWeight: 'bold',
                        fontSize: 9,
                        minWidth: 20,
                        height: 20,
                        p: '2px',
                        bgcolor: 'like.tap',
                      },
                    }}
                  />
                )}
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                {!(!thread.isOfficial && !thread.category) ? (
                  <ThreadCategory
                    isOfficial={thread.isOfficial}
                    category={thread.category ?? ''}
                  />
                ) : (
                  <Box />
                )}
                <Stack direction="row" alignItems="center" spacing={2}>
                  {!thread.isAllowNotification && (
                    <Icons.AudioOff
                      sx={{ color: 'grey.300' }}
                      fontSize="small"
                    />
                  )}
                  <Typography variant="caption" color="text.hint">
                    {thread.latestComment?.formattedCreatedAt ??
                      thread.formattedCreatedAt}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </ThreadBoxCell>
    </Card>
  )
}

const ThreadBoxCell: Cell<ThreadBoxCellProps> = ({
  isAccessible,
  isShowModalPersonalInfoRequire,
  id,
  artistId,
  displayName,
  children,
  onClick,
}) => {
  const { t } = useTranslation('artistThreads')
  const { routerPages, generatePath } = useRouter()
  const { showModal } = useSettingUserInfoModal()
  const [open, setOpen] = useState<boolean>(false)
  return (
    <Stack
      flex={1}
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
      }}
    >
      {children}
      <CardActionArea
        sx={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          zIndex: 1,
        }}
        onClick={() => {
          !isAccessible && setOpen(true)
          isShowModalPersonalInfoRequire && showModal()
          onClick && onClick()
        }}
        href={
          isAccessible && !isShowModalPersonalInfoRequire
            ? generatePath(routerPages.ArtistThreadDetail, { threadId: id })
            : ''
        }
      />
      <InformationModal
        open={open}
        onClose={() => setOpen(false)}
        title={t('viewMemberInfoTitle')}
        content={t('viewMemberInfoDesc', { displayName })}
        cancelText={t('close')}
        buttons={
          <Button
            size="large"
            color="primary"
            sx={{ minWidth: 212 }}
            href={generatePath(routerPages.ArtistMembership, { artistId })}
          >
            {t('seeDetail')}
          </Button>
        }
      />
    </Stack>
  )
}
